package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.GeoPoint
import diana.soleil.design.iraniancanadienbazzar.core.model.Metros
import diana.soleil.design.iraniancanadienbazzar.core.model.location
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.stateIn

/** UI state for the directory map: geocoded businesses + where to aim the camera pre-location. */
data class MapUiState(
    val businesses: List<Business> = emptyList(),
    val metroCenter: GeoPoint = DEFAULT_CENTER,
    /**
     * Whether a real metro is selected. False on "All Canada"/"All USA", where [metroCenter] is only
     * a placeholder — the map must NOT treat it as a "near me" centre, or every business outside
     * downtown Toronto disappears behind the radius filter.
     */
    val hasActiveMetro: Boolean = false,
) {
    private companion object
}

/** Toronto — the launch metro; the camera fallback when no metro is active and no fix exists. */
private val DEFAULT_CENTER = GeoPoint(43.6532, -79.3832)

/**
 * Streams the active metro's geocoded businesses for [MapScreen]. Address-only records (no lat/lng
 * yet) are filtered here — a map can't place them; they remain reachable via the list surfaces.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class MapViewModel(
    businessRepository: BusinessRepository,
    cityController: CityController,
) : ViewModel() {

    val state: StateFlow<MapUiState> =
        combine(
            combine(cityController.activeMetroId, cityController.activeCountry) { m, c -> m to c }
                .flatMapLatest { (metroId, country) ->
                    businessRepository.observeBusinesses(metroId = metroId, country = country.code)
                },
            cityController.activeMetroId,
        ) { result, metroId ->
            MapUiState(
                // Address-suppressed (home-based) listings are never pinned — no exact location.
                businesses = (result as? DataResult.Success)?.data.orEmpty()
                    .filter { it.showsExactLocation && it.location() != null },
                metroCenter = Metros.all.firstOrNull { it.id == metroId }
                    ?.let { GeoPoint(it.centerLat, it.centerLng) }
                    ?: DEFAULT_CENTER,
                hasActiveMetro = Metros.all.any { it.id == metroId },
            )
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), MapUiState())
}
